/**
 * Labstep
 *
 * @module screens/ExperimentWorkflow/Index/TableTimeline
 * @desc Experiment index with table or timeline view type
 */

import { BulkAction } from 'labstep-web/components/Entity/Search/BulkActions';
import { ExperimentWorkflowActionCreateMenu } from 'labstep-web/components/ExperimentWorkflow/Action/CreateMenu';
import { ExperimentWorkflowEmptyState } from 'labstep-web/components/ExperimentWorkflow/EmptyState';
import { ExperimentWorkflowListOrTable } from 'labstep-web/components/ExperimentWorkflow/ListOrTable';
import { withUiPersistent } from 'labstep-web/containers/UiPersistent';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { FolderType } from 'labstep-web/models/folder.model';
import { ExperimentWorkflowIndexBreadcrumb } from 'labstep-web/screens/ExperimentWorkflow/Index/Breadcrumb';
import { ExperimentWorkflowIndexTableTimelineLeftPane } from 'labstep-web/screens/ExperimentWorkflow/Index/TableTimeline/LeftPane';
import { MasterIndex } from 'labstep-web/screens/Master/Index';
import { selectFolderIncludeSubfolders } from 'labstep-web/state/new/user-settings';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { bulkActions, filters } from './constants';
import type { ExperimentWorkflowIndexTableTimelineProps } from './types';

export const ExperimentWorkflowIndexTableTimeline: React.FC<
  ExperimentWorkflowIndexTableTimelineProps
> = ({ group, uiPersistent }) => {
  const includeSubfolders = useSelector(
    selectFolderIncludeSubfolders(FolderType.experiment_workflow),
  );
  const bulkActionWithExperimentWorkflowFinish = useMemo(
    () =>
      group.is_completion_checklist_enabled
        ? bulkActions
        : [...bulkActions, BulkAction.experiment_workflow_finish],
    [group],
  );
  const action = useCallback(({ searchParams }: any) => {
    const folderId = searchParams.folder_id;
    return (
      <ExperimentWorkflowActionCreateMenu
        body={folderId ? { folder_id: folderId } : undefined}
      />
    );
  }, []);
  const params = useMemo(
    () => ({
      group_id: group.id,
      include_subfolders: includeSubfolders,
    }),
    [group.id, includeSubfolders],
  );
  const noResultsMessage = useCallback(
    ({ searchParams }: any) => (
      <ExperimentWorkflowEmptyState
        action={action({ searchParams })}
      />
    ),
    [action],
  );
  const breadcrumb = useMemo(
    () => <ExperimentWorkflowIndexBreadcrumb />,
    [],
  );
  const leftPane = useMemo(
    () => <ExperimentWorkflowIndexTableTimelineLeftPane />,
    [],
  );

  return (
    <MasterIndex
      breadcrumb={breadcrumb}
      leftPane={leftPane}
      params={params}
      noResultsMessage={noResultsMessage}
      filters={filters}
      action={action}
      entityName={ExperimentWorkflow.entityName}
      bulkActions={bulkActionWithExperimentWorkflowFinish}
      isCursor={uiPersistent.experiment_workflow_view === 'timeline'}
      hideAction
      showExperimentWorkflowViewManager
    >
      {({ entities, ...rest }: any): React.ReactElement => (
        <ExperimentWorkflowListOrTable
          experimentWorkflows={entities}
          {...rest}
        />
      )}
    </MasterIndex>
  );
};

export default withUiPersistent(ExperimentWorkflowIndexTableTimeline);
